import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, inject, Input, ViewContainerRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { MenuComponent } from './menu.component';
import { Position, POSITION_MAP } from '../../../../../util-common/src/lib/position.util';

@Directive({
  selector: '[ttMenuTriggerFor]',
  standalone: true,
  host: {
    class: 'tt-menu-trigger',
    '[class.tt-menu-trigger--disabled]': 'ttMenuDisabled',
  },
})
export class MenuTriggerDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);
  private viewContainerRef = inject(ViewContainerRef);

  private menuCloseSubscription = Subscription.EMPTY;
  private overlayOutsideClickSubscription = Subscription.EMPTY;
  private overlayRef: OverlayRef | null = null;
  private updatePositionTimeout: ReturnType<typeof setTimeout> | null = null;

  private _menu: MenuComponent;
  @Input('ttMenuTriggerFor') public set menu(menu: MenuComponent) {
    this._menu = menu;
    this.menuCloseSubscription.unsubscribe();

    if (menu) {
      this.menuCloseSubscription = menu.closed.subscribe(() => this.closeMenu());
    }
  }

  @Input() public ttMenuPosition: Position;
  @Input() public ttMenuDisabled = false;

  @HostListener('click', ['$event'])
  public onClick(event: Event) {
    event.stopPropagation();
    this.openMenu();
  }

  private openMenu(): void {
    if (!this._menu) {
      throw new Error('MenuTriggerDirective must have a "ttMenuTriggerFor" attribute');
    }

    if (this.ttMenuDisabled) {
      return;
    }

    this.closeMenu();

    const defaultPositions: Position[] = [Position.BOTTOM_RIGHT, Position.BOTTOM_LEFT, Position.BOTTOM_CENTER, Position.RIGHT_CENTER];

    const topFallbacks: ConnectedPosition[] = [
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
    ];

    const positions: ConnectedPosition[] = [...defaultPositions.map((pos) => ({ ...POSITION_MAP.get(pos) })), ...topFallbacks];

    if (this.ttMenuPosition) {
      positions.unshift({ ...POSITION_MAP.get(this.ttMenuPosition) });
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef.nativeElement)
      .withPositions(positions)
      .withViewportMargin(8)
      .withFlexibleDimensions(false)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    const portal = new TemplatePortal(this._menu.template, this.viewContainerRef);
    this.overlayRef.attach(portal);

    if (this.updatePositionTimeout) {
      clearTimeout(this.updatePositionTimeout);
    }

    this.updatePositionTimeout = setTimeout(() => {
      if (this.overlayRef) {
        this.overlayRef.updatePosition();
      }
    });

    this.overlayOutsideClickSubscription.unsubscribe();
    this.overlayOutsideClickSubscription = this.overlayRef.outsidePointerEvents().subscribe(() => this.closeMenu());
  }

  public closeMenu(): void {
    if (this.updatePositionTimeout) {
      clearTimeout(this.updatePositionTimeout);
      this.updatePositionTimeout = null;
    }

    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    this.overlayOutsideClickSubscription.unsubscribe();
  }

  ngOnDestroy(): void {
    this.menuCloseSubscription.unsubscribe();
    this.overlayOutsideClickSubscription.unsubscribe();
    this.closeMenu();
  }
}
